/**
 * Weight-aware wrappers around the integer-atom helpers used by
 * QuickInventoryCount.
 *
 * A product sold by weight is usually set up with one atom per unit
 * (e.g. "kg" with `quantity_in_unit_package` 1), so a count of 1.5 kg has
 * no integer representation and `Math.round` turned it into 2. These
 * helpers scale every atom by `WEIGHT_PRECISION` for weight products so
 * counts keep three decimals (gram precision for kg) while the arithmetic
 * stays integer. Non-weight products keep a scale of 1 — unchanged.
 */
export const WEIGHT_PRECISION = 1000;

export interface CountRow {
  id: string;
  unit: string;
  qty_in_package: number;
  is_sold_by_weight: boolean;
}

/**
 * Scaled atoms per parent unit. Precision is enabled when the parent or
 * any of its fraction rows is sold by weight, since each row carries its
 * own flag.
 */
export const countScaleOf = (item: {
  is_sold_by_weight?: boolean | null;
  store_product_fractions?: ReadonlyArray<{
    is_sold_by_weight?: boolean | null;
  }> | null;
}): number =>
  item.is_sold_by_weight ||
  item.store_product_fractions?.some((f) => f.is_sold_by_weight)
    ? WEIGHT_PRECISION
    : 1;

/** Round a count to the precision the input accepts. */
export const roundQty = (value: number): number =>
  Math.round(value * WEIGHT_PRECISION) / WEIGHT_PRECISION;

/** Sum per-row counts into scaled integer atoms. */
export const sumCountAtoms = (
  rows: CountRow[],
  quantities: Record<string, number>,
  scale: number,
): number =>
  rows.reduce(
    (sum, r) =>
      sum + Math.round((quantities[r.id] ?? 0) * r.qty_in_package * scale),
    0,
  );

/**
 * Greedy largest-pack-first split of (signed) scaled atoms into units per
 * row (same algorithm as `decomposeAtoms` in shared). Non-weight rows get
 * whole packs; a residue that no whole pack can hold goes, as a decimal, to
 * the smallest weight-sold row. Row order is preserved.
 */
export const decomposeCount = (
  atoms: number,
  rows: CountRow[],
  scale: number,
): Array<{ id: string; name: string; units: number }> => {
  const sign = atoms < 0 ? -1 : 1;
  let remaining = Math.abs(Math.round(atoms));
  const units = new Map<string, number>();

  for (const r of [...rows].sort(
    (a, b) => b.qty_in_package - a.qty_in_package,
  )) {
    const packAtoms = r.qty_in_package * scale;
    const whole = Math.floor(remaining / packAtoms);
    remaining -= whole * packAtoms;
    units.set(r.id, sign * whole);
  }

  const weightRow = [...rows]
    .filter((r) => r.is_sold_by_weight)
    .sort((a, b) => a.qty_in_package - b.qty_in_package)[0];
  if (remaining > 0 && weightRow) {
    units.set(
      weightRow.id,
      roundQty(
        (units.get(weightRow.id) ?? 0) +
          (sign * remaining) / (weightRow.qty_in_package * scale),
      ),
    );
  }

  return rows.map((r) => ({
    id: r.id,
    name: r.unit,
    units: units.get(r.id) ?? 0,
  }));
};
